import React, { lazy, Suspense, type FC, type ReactNode, useState, useEffect, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import {
  useSiteMetadata,
  useNavFooterMenu,
  useAnnouncementBanner,
  useGlobalNav,
  useSmartlingStrings,
} from '@lucid/hooks';
import { getLanguage, isBrowser } from '@lucid/utils';
import {
  AnnouncementBanner,
  Box,
  LayoutContext,
  Pingdom,
  SiteFooter,
  type SiteFooterOptionalProps,
  GlobalCSS,
  ThemeProvider,
  Delayed,
  NavBlogProvider,
  Navbar,
  ABTesting,
  type NavSectionOptions,
} from '@lucid/components';
import { theme } from '@lucid/theme';

import type { Lucid } from 'types/types';
import { useDefaultPageUrls } from 'hooks/hooks';

const CMSNodeButton = lazy(() => import('@lucid/components').then(module => ({ default: module.CMSNodeButton })));
const PingdomHydration = lazy(() => import('@lucid/components').then(module => ({ default: module.Pingdom })));

interface LayoutProps extends Lucid.Context.Page.Layout, Lucid.Context.Blog.Context.Search {
  children: ReactNode;
  navbarProps?: NavSectionOptions;
  footerProps?: SiteFooterOptionalProps;
  hasNavbar?: boolean | null;
  hasFooter?: boolean | null;
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  shortCode,
  pageType,
  children,
  nodeTranslations = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true,
  product,
  handleSearch,
  currentSearch,
  shouldDisplayBlogNav = false,
}) => {
  const { cmsUrl } = useSiteMetadata();
  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const { locale_id, url_lang_prefix, name } = getLanguage(shortCode);
  const defaultPageUrls = useDefaultPageUrls(shortCode) as Record<string, string>;
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_footer_language_switch'],
    shortCode,
  });
  const announcement = useAnnouncementBanner(shortCode, product);
  const hasGlobalNav = useGlobalNav(pageType, product);

  useEffect(() => {
    if (isBrowser()) {
      setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
    }
  }, []);

  const layoutContext = {
    shortCode,
    urlLangPrefix: url_lang_prefix,
    localeId: locale_id,
    product,
    pageType,
    nid,
  };

  // Footer props
  const footerMenu = useNavFooterMenu(1, shortCode);

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <GlobalCSS />
      <Pingdom id="pingdom-check" />
      <Delayed>
        <Suspense fallback={<div key="pingdomHydrationFallback" id="pingdom-hydration" />}>
          {isBrowser() && (
            <PingdomHydration id="pingdom-hydration-check">PINGDOM_CANARY_STRING_HYDRATION</PingdomHydration>
          )}
        </Suspense>
      </Delayed>
      <ThemeProvider product={product} theme={theme}>
        <ABTesting.GlobalProvider {...layoutContext}>
          <NavBlogProvider
            currentSearch={currentSearch}
            elasticEngine="coblog"
            handleSearch={handleSearch}
            shouldDisplayBlogNav={shouldDisplayBlogNav}
          >
            {hasNavbar && (
              <Navbar
                {...navbarProps}
                buttonAriaLabel={withSmartling('mktg_footer_language_switch', {
                  language: name,
                })}
                defaultPageUrls={defaultPageUrls}
                globalMenuId={6}
                hasGlobalNav={hasGlobalNav || shouldDisplayBlogNav}
                nodeTranslations={nodeTranslations}
                siteMenuId={{ scale: 5, spark: 38 }[product] ?? 1}
              />
            )}
            <Box as="main" className="gatsby-root" data-lang={shortCode} data-nid={nid} data-page-type={pageType}>
              {announcement && <AnnouncementBanner announcement={announcement} />}
              {children}
              <Suspense fallback={<div key="cmsNodeSuspenseFallback" id="cmsnode-suspense-fallback" />}>
                {shouldShowEditButton && (
                  <CMSNodeButton href={`${cmsUrl}/admin/content/${pageType.replace(/-/gi, '_')}/${nid}`} />
                )}
              </Suspense>
            </Box>
            {hasFooter && <SiteFooter {...footerProps} menu={footerMenu} nodeTranslations={nodeTranslations} />}
          </NavBlogProvider>
        </ABTesting.GlobalProvider>
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default Layout;
